import { formatCurrencyInputOnChange, formatDollars } from '@fruitful/fruitils';
import {
    type MembershipSpend,
    type Spend,
    SpendType,
} from '@fruitful/member-profile/src/spend/types/Spend';
import { AnalyticsUserEvent } from '@fruitful/types';
import { type Href, router } from 'expo-router';
import { useAtom } from 'jotai';
import { isEmpty, omit } from 'lodash';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Text, TextInput } from 'react-native';
import {
    BackButton,
    BackButtonIconType,
    BodyLargeEmphasized,
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    CheckIcon,
    EditIcon,
    FullScreenContainer,
    H5Regular,
    Icon,
    isWeb,
    Separator,
    useToastController,
    View,
    XIcon,
    XStack,
    YStack,
} from 'smoothie';

import { membershipSpendAtom } from '../../bills/state/BillsAtoms';
import { useViewEvents } from '../../events/hooks/useViewEvents';
import { track } from '../../logger/Logger';
import useUpsertMembershipSpend from '../../member-profile/hooks/useUpsertMembershipSpend';
import { EditSpendSheet } from './EditSpendSheet';

const parseAmount = (val: string) => parseFloat(val.replace(/[^0-9.-]+/g, ''));
const isAmountEmpty = (val: string) => isEmpty(val.replace(/[^0-9.-]+/g, ''));

const SpendHeader = ({ fallbackRoute }: { fallbackRoute: Href }) => {
    return (
        <XStack
            alignItems="center"
            justifyContent="space-between"
            paddingHorizontal={2}
            paddingTop={3}
            paddingBottom={2}
            width="100%"
        >
            <BackButton
                fallbackRoute={fallbackRoute}
                iconType={BackButtonIconType.ARROW_LEFT}
            />
            <BodyLargeEmphasized>Spend</BodyLargeEmphasized>
            <View width={40} />
        </XStack>
    );
};

interface SpendReviewProps {
    context: 'profile' | 'onboarding' | 'before-money-map-session';
}

export const SpendReview = ({ context }: SpendReviewProps) => {
    // Determine the origin route: prioritize onboarding context, then use originRoute if provided
    const determinedOriginRoute =
        context === 'onboarding'
            ? '/'
            : context === 'before-money-map-session'
              ? '/home'
              : '/settings';

    const { trackViewOnce } = useViewEvents();
    const trackViewPrefix = context === 'onboarding' ? 'Onboarding' : 'Profile';
    trackViewOnce(`${trackViewPrefix}/SpendReview`);
    const spendInputRef = useRef<TextInput>(null);

    const [membershipSpend, setMembershipSpend] = useAtom(membershipSpendAtom);
    const [spendEditorOpen, setSpendEditorOpen] = useState(
        membershipSpend?.amount != null,
    );
    const [disclosureIsVisible, setDisclosureIsVisible] = useState(
        membershipSpend?.amount == null,
    );

    useEffect(() => {
        if (disclosureIsVisible && spendEditorOpen && spendInputRef.current) {
            spendInputRef.current.focus();
        }
    }, [spendEditorOpen]);

    const toast = useToastController();
    const [spendAmount, setSpendAmount] = useState(() =>
        membershipSpend?.amount != null
            ? formatDollars(membershipSpend.amount, false)
            : '$',
    );
    const [editSpendSheetOpen, setEditSpendSheetOpen] = useState(false);
    const { mutate: submitEdit } = useUpsertMembershipSpend({
        onSuccess: (data) => {
            setMembershipSpend(data);
            const message = data.confirmed
                ? 'Spend reviewed'
                : 'Spend amount updated';
            toast.show(message, {
                icon: (
                    <View
                        alignItems="center"
                        justifyContent="center"
                        backgroundColor="$green1000"
                        borderRadius={12}
                        padding={9}
                    >
                        <Icon icon={CheckIcon} size={24} color="$white" />
                    </View>
                ),
            });
            if (data.confirmed) {
                router.navigate(determinedOriginRoute as Href);
            }
        },
        onError: () => {
            toast.show('Error updating spend. Please try again.', {
                icon: (
                    <View
                        alignItems="center"
                        justifyContent="center"
                        backgroundColor="$red1000"
                        borderRadius={12}
                        padding={9}
                    >
                        <Icon icon={XIcon} size={24} color="$white" />
                    </View>
                ),
            });
        },
    });

    return (
        <FullScreenContainer
            header={
                <SpendHeader fallbackRoute={determinedOriginRoute as Href} />
            }
            footer={
                <XStack gap="$2">
                    <View flex={1}>
                        <Button
                            text="Confirm"
                            variant={ButtonVariant.PRIMARY}
                            size={ButtonSize.LARGE}
                            marginTop="auto"
                            disabled={isAmountEmpty(spendAmount)}
                            onPress={() => {
                                setDisclosureIsVisible(false);

                                let safeSpend = omit(membershipSpend, [
                                    'createdAt',
                                    'updatedAt',
                                    'membershipId',
                                    'prevMonth1Amount',
                                    'prevMonth2Amount',
                                    'prevMonth3Amount',
                                    'period',
                                ]);
                                const convertedAmount = parseFloat(
                                    spendAmount.replace(/[^0-9.-]+/g, ''),
                                );

                                if (!membershipSpend) {
                                    safeSpend = {
                                        type: SpendType.MANUAL,
                                    };
                                }

                                const requestBody = {
                                    ...safeSpend,
                                    amount: convertedAmount,
                                    confirmed: true,
                                };

                                submitEdit({ body: requestBody as Spend });

                                track(
                                    AnalyticsUserEvent.ONBOARDING_FUNNEL_SPEND_COMPLETED,
                                    undefined,
                                    true,
                                );
                                track(
                                    AnalyticsUserEvent.ONBOARDING_FUNNEL_EXPENSES_COMPLETED,
                                    undefined,
                                    true,
                                );
                            }}
                        />
                    </View>
                </XStack>
            }
        >
            <YStack width="100%" gap={2} paddingBottom={6} marginTop={16}>
                <H5Regular>Review your monthly spend amount</H5Regular>
                <BodyMediumRegular color="$gray900">
                    Your monthly spend includes expenses like groceries, gas,
                    shopping, eating out, fun stuff, and more.
                </BodyMediumRegular>
                <BodyMediumRegular color="$gray900">
                    This number is estimated from your spending history. You can
                    update it if needed.
                </BodyMediumRegular>
            </YStack>
            <YStack
                marginTop={24}
                width="100%"
                borderRadius={20}
                backgroundColor="$gray100"
            >
                {spendEditorOpen ? (
                    <YStack marginVertical={32} alignItems="center" gap={8}>
                        <BodyMediumRegular color="$gray900">
                            Average monthly spend
                        </BodyMediumRegular>
                        <XStack
                            gap={8}
                            justifyContent="center"
                            alignItems="flex-start"
                        >
                            {isWeb ? (
                                // hack to get the text input to not stretch on web and center itself with the icon
                                <View
                                    style={{
                                        position: 'relative',
                                        display: 'inline-block',
                                    }}
                                >
                                    <Text
                                        style={{
                                            fontSize: 40,
                                            fontWeight: '500',
                                            opacity: 0,
                                            paddingHorizontal: 4,
                                        }}
                                    >
                                        {spendAmount || '$'}
                                    </Text>
                                    <TextInput
                                        ref={spendInputRef}
                                        editable
                                        keyboardType="number-pad"
                                        value={spendAmount}
                                        onChangeText={(text: string) => {
                                            const newAmount =
                                                formatCurrencyInputOnChange(
                                                    spendAmount ?? '$',
                                                    text,
                                                ).replace(' ', '');
                                            setSpendAmount(newAmount);
                                        }}
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            fontSize: 40,
                                            fontWeight: '500',
                                            textAlign: 'center',
                                            width: '100%',
                                            height: '100%',
                                            padding: 0,
                                            borderWidth: 0,
                                            backgroundColor: 'transparent',
                                        }}
                                    />
                                </View>
                            ) : (
                                <View alignSelf="flex-start">
                                    <TextInput
                                        ref={spendInputRef}
                                        editable
                                        keyboardType="number-pad"
                                        value={spendAmount}
                                        onChangeText={(text: string) => {
                                            const newAmount =
                                                formatCurrencyInputOnChange(
                                                    spendAmount ?? '$',
                                                    text,
                                                ).replace(' ', '');
                                            setSpendAmount(newAmount);
                                        }}
                                        style={{
                                            fontSize: 40,
                                            fontWeight: '500',
                                            textAlign: 'center',
                                        }}
                                    />
                                </View>
                            )}
                            <View
                                onPress={() => spendInputRef.current?.focus()}
                            >
                                <Icon color="$gray800" icon={EditIcon} />
                            </View>
                        </XStack>
                    </YStack>
                ) : (
                    <YStack marginVertical={32} alignItems="center" gap={8}>
                        <BodyMediumRegular color="$gray900">
                            Average monthly spend
                        </BodyMediumRegular>
                        <BodyMediumRegular
                            color="$gray1100"
                            fontSize={40}
                            fontWeight="500"
                            lineHeight={48}
                        >
                            ----
                        </BodyMediumRegular>
                    </YStack>
                )}
                {disclosureIsVisible && (
                    <YStack width="100%">
                        <Separator />
                        <YStack alignItems="center" paddingHorizontal={24}>
                            <BodyMediumRegular color="$gray1000" marginTop={32}>
                                Looks like we didn’t find any spending data from
                                your connected accounts.
                            </BodyMediumRegular>
                            <BodyMediumRegular
                                color="$gray1000"
                                marginVertical={32}
                            >
                                Add your average monthly spend based on your own
                                best estimate.
                            </BodyMediumRegular>
                        </YStack>
                        {!spendEditorOpen && (
                            <YStack
                                flex={1}
                                paddingHorizontal={isWeb ? 40 : 24}
                                paddingBottom={24}
                            >
                                <Button
                                    text="Add average monthly spend"
                                    variant={ButtonVariant.PRIMARY}
                                    size={ButtonSize.LARGE}
                                    marginTop="auto"
                                    onPress={() => {
                                        setSpendEditorOpen(true);
                                    }}
                                />
                            </YStack>
                        )}
                    </YStack>
                )}
            </YStack>
            {editSpendSheetOpen && (
                <EditSpendSheet
                    onClose={() => {
                        setEditSpendSheetOpen(false);
                    }}
                    onSubmit={(
                        membershipSpend: MembershipSpend | null,
                        amount: string,
                    ) => {
                        const convertedAmount = parseAmount(amount);
                        const safeSpend = omit(membershipSpend, [
                            'createdAt',
                            'updatedAt',
                            'membershipId',
                            'prevMonth1Amount',
                            'prevMonth2Amount',
                            'prevMonth3Amount',
                            'period',
                        ]);

                        const requestBody = {
                            ...safeSpend,
                            amount: convertedAmount,
                            confirmed: false,
                        };

                        submitEdit({ body: requestBody as Spend });

                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-expect-error
                        setMembershipSpend({
                            ...membershipSpend,
                            amount: convertedAmount,
                        });
                    }}
                />
            )}
        </FullScreenContainer>
    );
};
